import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Instagram, Truck, Award, Sparkles } from "lucide-react";
import ProductCard from "../components/ProductCard";
import { api } from "../lib/api";

// Every image below is from MAKEBA Design & Prints - her own CDN or her own brand assets.
const HERO_IMG = "/products/newcatalog/custom-travel-bag/1.jpg";
const HERO_TAG_PRODUCT = "Custom Travel Bag";
const HERO_TAG_PRICE = "$169.99";

const categoryTiles = [
  {
    slug: "travel",
    label: "Travel",
    tagline: "Waterproof · Statement",
    img: "/products/newcatalog/signature-travel-bag-she-be-trippin-pink-black/1.jpg",
  },
  {
    slug: "tshirts",
    label: "Apparel",
    tagline: "Print with purpose",
    img: "/products/newcatalog/mentally-offline-tee/1.png",
  },
  {
    slug: "backpacks",
    label: "Backpacks",
    tagline: "The iconic collection",
    img: "/products/newcatalog/signature-backpack-chelini-multi-graffiti/3.jpg",
    objPos: "18% 50%",
  },
];

const secondaryTiles = [
  { slug: "hbcu", label: "HBCU Collection", img: "/products/newcatalog/langston-travel-bag-blue/1.jpg" },
  { slug: "totes", label: "Totes & Bags", img: "/products/newcatalog/signature-travel-bag-asia-red/1.jpg" },
  { slug: "kids", label: "Kids & Puzzles", img: "/products/newcatalog/signature-backpack-dinosaur-stem-green/1.jpg" },
];

const marqueeItems = [
  "PRINT WITH PURPOSE",
  "CUSTOM DESIGNS · MADE TO ORDER",
  "SCREEN PRINT · EMBROIDERY · BRANDED MERCH",
  "STEAM-2-GO EDUCATIONAL KITS",
  "OFFICIALLY LICENSED LANGSTON U",
  "BLACK-OWNED · OKLAHOMA-MADE",
  "FEATURED IN ESSENCE MAGAZINE",
  "20 YEARS IN THE CRAFT",
];

const testimonials = [
  {
    quote: "Makeba's travel bags are the only ones I take on tour. Sturdy, statement, and always spark conversations at the airport.",
    name: "Alicia W.",
    role: "Traveler · Atlanta",
  },
  {
    quote: "Twenty years of craft in every stitch. Every custom shirt we've ordered has been immaculate.",
    name: "Rev. Marcus B.",
    role: "Longtime customer",
  },
  {
    quote: "We ordered 300 shirts for our reunion. Perfect print, perfect service. She listens.",
    name: "Dr. Jasmine T.",
    role: "AKA · Kappa chapter",
  },
];

// -----------------------------------------------------------------------------
// Ella-inspired: Tabbed featured products with switchable collections
// -----------------------------------------------------------------------------
const FeaturedTabs = ({ featured, newArrivals }) => {
  const [tab, setTab] = React.useState("bestsellers");

  const tabs = [
    { key: "bestsellers", label: "Best Sellers", data: featured.slice(0, 8) },
    { key: "new", label: "New Arrivals", data: newArrivals },
    { key: "custom", label: "Custom Favorites", data: featured.filter((p) => p.slug.includes("custom")).concat(featured).slice(0, 8) },
  ];

  const active = tabs.find((t) => t.key === tab) || tabs[0];

  return (
    <section data-testid="featured-tabs" className="container-x py-20 md:py-24">
      <div className="text-center mb-10">
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-3">Explore</div>
        <h2 className="font-display font-light text-4xl sm:text-5xl text-black tracking-tight">
          This season's <span className="font-black">favorites.</span>
        </h2>
        <div className="mt-8 inline-flex items-center gap-6 border-b border-[#E5E2DA] pb-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`text-[11px] tracking-[0.24em] uppercase font-semibold pb-3 transition ${
                tab === t.key
                  ? "text-black border-b-2 border-[#EC008C] -mb-[1px]"
                  : "text-[#4A4A4A] hover:text-black"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
        {active.data.slice(0, 8).map((p, i) => (
          <ProductCard key={p.id + tab} product={p} index={i} />
        ))}
      </div>

      <div className="text-center mt-12">
        <Link to="/shop" data-testid="tabs-view-more" className="btn-outline inline-flex">
          View more <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
};

// -----------------------------------------------------------------------------
// Ella-inspired: Shoppable lookbook with numbered hotspots
// -----------------------------------------------------------------------------
const ShoppableLookbook = ({ featured }) => {
  const [openPin, setOpenPin] = React.useState(1);

  const pins = [
    { id: 1, x: 30, y: 42, slug: "custom-backpack", name: "Custom Backpack", price: 89.99 },
    { id: 2, x: 62, y: 28, slug: "world-traveler-pink-small", name: "World Traveler Pink", price: 59.99 },
    { id: 3, x: 55, y: 72, slug: "atl-about-that-legacy-tee", name: "ATL Legacy Tee", price: 25.0 },
  ];

  const activePin = pins.find((p) => p.id === openPin);
  const activeProduct = featured.find((p) => p.slug === activePin?.slug);

  return (
    <section data-testid="shoppable-lookbook" className="container-x pb-24">
      <div className="grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-8 relative">
          <div className="aspect-[4/5] md:aspect-[16/10] overflow-hidden relative bg-[#F0EDE6]">
            <img
              src="/products/newcatalog/signature-backpack-nia-science-pink/1.jpg"
              alt="Shop the look"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            {pins.map((p) => (
              <button
                key={p.id}
                data-testid={`lookbook-pin-${p.id}`}
                onClick={() => setOpenPin(p.id)}
                className={`absolute w-9 h-9 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-black flex items-center justify-center text-xs font-bold transition-all ${
                  openPin === p.id ? "bg-[#EC008C] text-white scale-110" : "bg-white text-black hover:bg-[#F3E30A]"
                }`}
                style={{ left: `${p.x}%`, top: `${p.y}%` }}
                aria-label={`Show ${p.name}`}
              >
                {p.id}
              </button>
            ))}
          </div>
        </div>

        <div className="md:col-span-4">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-3">Shop the look</div>
          <h3 className="font-display font-light text-3xl md:text-4xl text-black tracking-tight mb-6">
            Get the <span className="font-black">signature look.</span>
          </h3>
          <p className="text-sm text-[#4A4A4A] font-light mb-8 leading-relaxed">
            Click any numbered pin on the photo to reveal details on the piece and shop it directly.
          </p>

          {activeProduct && (
            <div data-testid="lookbook-active-product" className="border border-[#E5E2DA] p-5">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full border-2 border-black bg-[#EC008C] text-white flex items-center justify-center text-xs font-bold">
                  {activePin.id}
                </div>
                <div className="flex-1">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[#4A4A4A] mb-1">Featured piece</div>
                  <div className="font-semibold text-black leading-snug">{activeProduct.name}</div>
                  <div className="font-black text-lg mt-1">${activeProduct.price.toFixed(2)}</div>
                  <Link
                    to={`/product/${activeProduct.slug}`}
                    data-testid="lookbook-shop-btn"
                    className="mt-4 inline-flex text-[11px] tracking-[0.22em] uppercase font-semibold text-black link-under"
                  >
                    Shop this piece →
                  </Link>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex gap-2">
            {pins.map((p) => (
              <button
                key={p.id}
                onClick={() => setOpenPin(p.id)}
                className={`w-9 h-9 rounded-full border-2 text-xs font-bold ${
                  openPin === p.id ? "bg-black text-white border-black" : "bg-white text-black border-[#E5E2DA] hover:border-black"
                }`}
              >
                {p.id}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [hero, setHero] = useState({
    hero_image: "/hero/lu-homecoming.webp",
    hero_link: "/homecoming",
    hero_alt: "Langston University Homecoming",
  });

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/products").then((r) => setNewArrivals(r.data.slice(0, 8))).catch(() => {});
    api.get("/site-config").then((r) => setHero(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page" className="bg-white">
      {/* HERO - admin-configurable homepage banner */}
      <section data-testid="hero-lu-homecoming" className="relative bg-white">
        <Link
          to={hero.hero_link || "/homecoming"}
          data-testid="hero-lu-cta"
          aria-label="Shop the featured collection"
          className="block w-full group focus:outline-none focus:ring-4 focus:ring-[#EC008C]/40"
        >
          <motion.img
            src={hero.hero_image}
            alt={hero.hero_alt}
            className="w-full h-auto block select-none transition-transform duration-700 group-hover:scale-[1.01]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            draggable={false}
            fetchpriority="high"
            loading="eager"
          />
        </Link>
        {/* Marquee */}
        <div className="border-t border-b border-black bg-white text-black py-3 overflow-hidden">
          <div className="marquee-track">
            {[...marqueeItems, ...marqueeItems, ...marqueeItems].map((t, i) => (
              <div key={i} className="flex items-center gap-6 px-6 font-semibold uppercase tracking-[0.25em] text-xs whitespace-nowrap">
                <span className="text-[#EC008C]">✦</span> {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORY TILES - real product photos */}
      <section className="container-x py-20 md:py-24">
        <div className="text-center mb-12">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-3">Shop by category</div>
          <h2 className="font-display font-light text-4xl sm:text-5xl text-black tracking-tight">
            Curated <span className="font-black">for every day.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {categoryTiles.map((c) => (
            <Link key={c.slug} to={`/shop?category=${c.slug}`} data-testid={`tile-${c.slug}`} className="tile-cat group">
              <div className="aspect-[4/5] overflow-hidden bg-[#FAFAF7]">
                <img src={c.img} alt={c.label} className="w-full h-full object-cover" style={{ objectPosition: c.objPos || "center" }} loading="lazy" />
              </div>
              <div className="pt-5 flex items-center justify-between gap-4">
                <div>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[#4A4A4A]">{c.tagline}</div>
                  <h3 className="font-display font-bold text-2xl md:text-3xl text-black mt-1">{c.label}</h3>
                </div>
                <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-black group-hover:text-[#EC008C] flex items-center gap-1">
                  Shop <ArrowRight size={14} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* EDITORIAL FEATURE BANNER - using her Custom Travel Bag */}
      <section className="relative bg-[#F0EDE6]">
        <div className="container-x py-20 md:py-28 grid md:grid-cols-2 gap-10 items-center">
          <div className="aspect-[4/5] overflow-hidden">
            <img
              src="/products/newcatalog/signature-travel-bag-ceo-mode-black/1.jpg"
              alt="Custom Travel Bag detail"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <div className="text-[11px] tracking-[0.32em] uppercase font-semibold mb-4 text-[#EC008C]">The Travel Edit</div>
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.05] tracking-tight text-black">
              Bags that <br />
              <span className="font-black">travel with intention.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg font-light max-w-md text-[#4A4A4A] leading-relaxed">
              Waterproof. Handmade. Unmistakable. Our travel line is designed for the woman who packs a purpose.
            </p>
            <Link to="/shop?category=travel" data-testid="banner-travel-cta" className="mt-8 btn-primary inline-flex">
              Explore travel <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* MEET MAKEBA */}
      <section className="bg-[#FAFAF7] py-24 md:py-32">
        <div className="container-x grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden bg-[#F0EEE9]">
              <img
                src="/brand/makeba.png"
                alt="Makeba Griffin, founder"
                className="w-full h-full object-cover"
                style={{ objectPosition: "50% 0%", transform: "scale(1.55)", transformOrigin: "50% 20%" }}
                loading="lazy"
              />
            </div>
          </div>
          <div className="md:col-span-7 md:pl-6">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-4">
              Meet the maker
            </div>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-black tracking-tight">
              Hi, I'm <span className="font-black">Makeba.</span>
            </h2>
            <div className="mt-2 font-script text-3xl text-[#EC008C]">a maker, a designer, a mother.</div>

            <p className="mt-8 text-base md:text-lg text-[#4A4A4A] leading-relaxed font-light">
              I've spent more than <strong className="text-black font-semibold">20 years in graphic design, illustration,
              branding, and custom apparel</strong> - with a Master's in Business Intelligence backing every decision.
              We specialize in <strong className="text-black font-semibold">branding, custom graphic design, screen printing,
              embroidery, apparel &amp; merchandise, print &amp; digital marketing materials, and custom illustrations.</strong>
            </p>
            <p className="mt-4 text-base md:text-lg text-[#4A4A4A] leading-relaxed font-light">
              We're the <strong className="text-black font-semibold">officially licensed collegiate vendor for
              Langston University</strong> - Oklahoma's only HBCU - and the creator of
              <strong className="text-black font-semibold"> Oklahoma's first STEAM-2-GO kits</strong>, designed to inspire
              creativity, encourage exploration, and promote representation through hands-on learning.
            </p>
            <p className="mt-4 text-base md:text-lg text-[#4A4A4A] leading-relaxed font-light">
              What started at a kitchen table in Oklahoma has grown into a nationally recognized brand - featured by
              <strong className="text-black font-semibold"> ESSENCE Magazine</strong> and honored as
              <strong className="text-black font-semibold"> 2025 Home-Based Small Business of the Year</strong>.
            </p>

            <blockquote className="mt-10 border-l-2 border-[#EC008C] pl-6 py-2">
              <div className="font-script text-2xl md:text-3xl leading-tight text-black">
                "Design isn't decoration. It's how we tell the world who we are."
              </div>
              <div className="mt-3 text-[11px] font-semibold tracking-[0.24em] uppercase text-[#4A4A4A]">- Makeba Griffin</div>
            </blockquote>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/designer" data-testid="home-cta-designer" className="btn-primary">
                Design with me <ArrowRight size={14} />
              </Link>
              <Link to="/contact" data-testid="home-cta-contact" className="btn-outline">
                Say hello
              </Link>
            </div>

            <div className="mt-12 pt-8 border-t border-[#E5E2DA] flex flex-wrap gap-x-8 gap-y-3 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#4A4A4A]">
              <span>ESSENCE Magazine</span>
              <span>Langston U</span>
              <span>OKSBDC 2025</span>
              <span>She Unplugged</span>
            </div>
          </div>
        </div>
      </section>

      {/* STEAM-2-GO CTA STRIP */}
      <section data-testid="home-steam-strip" className="bg-black text-white relative overflow-hidden border-y-2 border-black">
        <div className="container-x py-16 md:py-20 grid md:grid-cols-12 gap-8 items-center relative z-10">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 bg-[#DCE842] text-black text-[11px] tracking-[0.28em] uppercase font-black px-3 py-1.5 border-2 border-[#DCE842] mb-5">
              <Sparkles size={12} /> STEAM-2-Go™ Kits
            </div>
            <h2 className="font-display font-black text-3xl md:text-5xl leading-[1.05] tracking-tight text-white">
              Screen-free discovery kits built for the <span className="text-[#DCE842]">next generation of trailblazers</span>.
            </h2>
            <p className="mt-5 text-white/80 font-light text-base md:text-lg max-w-2xl leading-relaxed">
              Hands-on kits pairing real science with pioneer Black, Indigenous, Latinx and women trailblazers.
              NGSS aligned. Grant funding eligible. Designed for classrooms and kitchen tables alike.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/steam"
                data-testid="home-steam-explore"
                className="inline-flex items-center gap-2 bg-[#EC008C] text-white px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-[#EC008C] hover:bg-white hover:text-black hover:border-white transition-colors"
              >
                Explore STEAM Kits <ArrowRight size={14} />
              </Link>
              <a
                href="/steam/steam-2-go-grant-catalog.pdf"
                download="MAKEBA-STEAM-2-Go-Grant-Catalog.pdf"
                target="_blank"
                rel="noreferrer"
                data-testid="home-steam-grant"
                className="inline-flex items-center gap-2 bg-transparent text-white px-6 py-3.5 text-[12px] tracking-[0.24em] uppercase font-black border-2 border-white hover:bg-white hover:text-black transition-colors"
              >
                District / Grant Catalog
              </a>
            </div>
          </div>
          <div className="md:col-span-4">
            <div className="relative aspect-[4/5] overflow-hidden border-2 border-[#DCE842]">
              <img src="/steam/kits-lineup.jpg" alt="STEAM-2-Go kits lineup" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* TABBED FEATURED PRODUCTS (Ella-style) */}
      <FeaturedTabs featured={featured} newArrivals={newArrivals} />

      {/* LOOKBOOK - 2-column using her products */}
      <section className="container-x pb-24">
        <div className="grid md:grid-cols-2 gap-6">
          <Link to="/shop?category=hbcu" data-testid="lookbook-apparel" className="tile-cat group">
            <div className="aspect-[4/5] overflow-hidden bg-[#FAFAF7]">
              <img
                src="/products/newcatalog/langston-backpack-blue/1.jpg"
                alt="Langston U sweatshirt"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="pt-5">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#4A4A4A]">The heritage edit</div>
              <h3 className="font-display font-bold text-3xl md:text-4xl text-black mt-1">HBCU Apparel</h3>
              <div className="mt-3 text-[11px] tracking-[0.22em] uppercase font-semibold text-black group-hover:text-[#EC008C] flex items-center gap-1">
                Shop the edit <ArrowRight size={14} />
              </div>
            </div>
          </Link>
          <Link to="/shop?category=travel" data-testid="lookbook-travel" className="tile-cat group">
            <div className="aspect-[4/5] overflow-hidden bg-[#FAFAF7]">
              <img
                src="/products/newcatalog/custom-travel-bag/1.jpg"
                alt="Custom travel bag lifestyle"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="pt-5">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#4A4A4A]">Adventure ready</div>
              <h3 className="font-display font-bold text-3xl md:text-4xl text-black mt-1">Travel</h3>
              <div className="mt-3 text-[11px] tracking-[0.22em] uppercase font-semibold text-black group-hover:text-[#EC008C] flex items-center gap-1">
                Shop the edit <ArrowRight size={14} />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* SECONDARY CATEGORY TILES */}
      <section className="container-x pb-24">
        <div className="grid md:grid-cols-3 gap-6">
          {secondaryTiles.map((c) => (
            <Link key={c.slug} to={`/shop?category=${c.slug}`} data-testid={`tile-${c.slug}`} className="tile-cat group">
              <div className="aspect-[4/5] overflow-hidden bg-[#FAFAF7]">
                <img src={c.img} alt={c.label} className="w-full h-full object-cover" style={{ objectPosition: c.objPos || "center" }} loading="lazy" />
              </div>
              <div className="pt-4">
                <h3 className="font-display font-bold text-xl md:text-2xl text-black">{c.label}</h3>
                <div className="mt-2 text-[10px] tracking-[0.24em] uppercase font-semibold text-[#EC008C]">Shop →</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* SHOPPABLE LOOKBOOK - numbered hotspots (Ella-style) */}
      <ShoppableLookbook featured={featured} />

      {/* NEW ARRIVALS */}
      <section className="container-x py-20 md:py-24 border-t border-[#E5E2DA]">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-3">New arrivals</div>
            <h2 className="font-display font-light text-4xl sm:text-5xl text-black tracking-tight">
              Fresh from <span className="font-black">the studio.</span>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
          {newArrivals.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#111111] text-white py-24 md:py-32">
        <div className="container-x">
          <div className="text-center mb-16">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#F3E30A] font-semibold mb-3">Customer stories</div>
            <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight">
              Real people. <span className="font-black">Real love.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {testimonials.map((t, i) => (
              <div key={i} className="border-t border-white/20 pt-8">
                <div className="text-[#EC008C] text-3xl leading-none mb-4">"</div>
                <p className="text-base md:text-lg font-light leading-relaxed text-white/90">{t.quote}</p>
                <div className="mt-6">
                  <div className="text-sm font-semibold">{t.name}</div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-white/50 mt-1">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* JOURNAL / STORY PREVIEW (Ella-style) */}
      <section className="container-x py-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-3">The Journal</div>
            <h2 className="font-display font-light text-4xl sm:text-5xl text-black tracking-tight">
              Stories from <span className="font-black">the studio.</span>
            </h2>
          </div>
          <Link to="/contact" className="text-[11px] tracking-[0.22em] uppercase font-semibold link-under">
            Read all stories
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              date: "02/05/26",
              title: "The Art of Everyday Craft",
              excerpt: "Twenty years at the press taught me one thing - beautiful design is made, not manufactured.",
              img: "/products/newcatalog/signature-backpack-nia-science-pink/1.jpg",
              tag: "Craft",
            },
            {
              date: "01/28/26",
              title: "Behind the Langston Collection",
              excerpt: "How a licensing deal turned into the collection I'm most proud to put my name on.",
              img: "/products/newcatalog/langston-travel-bag-blue/1.jpg",
              tag: "Collection",
            },
            {
              date: "01/12/26",
              title: "Why We Print with Purpose",
              excerpt: "Every stitch tells a story. Here's why intention matters more than trends.",
              img: "/products/newcatalog/signature-travel-bag-summer-purple-graffiti/1.jpg",
              tag: "Philosophy",
            },
          ].map((j, i) => (
            <article key={i} data-testid={`journal-card-${i}`} className="group">
              <div className="aspect-[5/4] overflow-hidden mb-5">
                <img src={j.img} alt={j.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" loading="lazy" />
              </div>
              <div className="flex items-center gap-3 text-[10px] tracking-[0.24em] uppercase font-semibold text-[#4A4A4A] mb-3">
                <span>{j.date}</span>
                <span className="text-[#EC008C]">·</span>
                <span>{j.tag}</span>
              </div>
              <h3 className="font-display font-bold text-xl md:text-2xl text-black leading-snug group-hover:text-[#EC008C] transition">{j.title}</h3>
              <p className="mt-3 text-sm text-[#4A4A4A] font-light leading-relaxed">{j.excerpt}</p>
              <div className="mt-4 text-[11px] tracking-[0.22em] uppercase font-semibold text-black link-under inline-block">Read story →</div>
            </article>
          ))}
        </div>
      </section>

      {/* CUSTOM ORDERS - using her Custom Backpack */}
      <section className="relative bg-[#FAFAF7] py-24 md:py-32">
        <div className="container-x grid md:grid-cols-2 gap-12 items-center">
          <div className="aspect-[5/6] overflow-hidden order-2 md:order-1">
            <img
              src="/products/newcatalog/signature-backpack-nia-science-pink/1.jpg"
              alt="Custom Backpack detail"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="order-1 md:order-2">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-4">Custom orders</div>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-black tracking-tight">
              Your story, <br />
              <span className="font-black">printed with intention.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#4A4A4A] leading-relaxed font-light max-w-lg">
              From 10 pieces for a boutique launch to 10,000 for a national campaign - we bring the same
              care to every order. Design in-studio, and we'll bring it to life.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/designer" data-testid="editorial-cta-designer" className="btn-primary">
                Open design studio <ArrowRight size={14} />
              </Link>
              <Link to="/contact" data-testid="editorial-cta-contact" className="btn-outline">
                Talk to Makeba
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* INSTAGRAM STRIP - her products */}
      <section className="container-x py-20">
        <div className="text-center mb-10">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#EC008C] font-semibold mb-3">Follow along</div>
          <h2 className="font-display font-bold text-3xl md:text-4xl text-black tracking-tight flex items-center justify-center gap-3">
            <Instagram size={28} /> @makebadesigns
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-1">
          {[
            "/products/newcatalog/signature-backpack-mason-astronomy-grey/1.jpg",
            "/products/newcatalog/signature-backpack-harper-reader-teal/1.jpg",
            "/products/newcatalog/signature-backpack-stem-level-up-black/1.jpg",
            "/products/newcatalog/signature-backpack-zuri-art-purple/1.jpg",
            "/products/newcatalog/signature-travel-bag-summer-purple-graffiti/1.jpg",
            "/products/newcatalog/signature-backpack-dinosaur-stem-green/1.jpg",
          ].map((src, i) => (
            <a
              key={i}
              href="https://instagram.com/makebadesigns"
              target="_blank"
              rel="noreferrer"
              className="aspect-square overflow-hidden group"
              data-testid={`ig-${i}`}
            >
              <img src={src} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-700" loading="lazy" />
            </a>
          ))}
        </div>
      </section>

      {/* PRESS STRIP */}
      <section className="border-y border-[#E5E2DA] py-12">
        <div className="container-x">
          <div className="text-center text-[10px] tracking-[0.28em] uppercase text-[#4A4A4A] font-semibold mb-6">
            As Featured In
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 text-lg md:text-xl font-display font-light text-[#4A4A4A]">
            <span className="font-black italic">ESSENCE</span>
            <span className="font-black">Langston University</span>
            <span>OKSBDC</span>
            <span className="italic">She Unplugged</span>
            <span>Perry Publishing</span>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
